<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            userTypes: {
                administrateur: "Administrateur",
                owner: "Super Admin",
                employe: "Employé",
                employe_read_only: "Utilisateur",
                admin: "Admin",
                member: "Membre",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface User {
    firstname?: string
    lastname?: string
    username?: string
    phone?: string
    img?: string
}

/**
 * Types d'utilisateur disponibles
 */
type UserType =
    | "administrateur"
    | "owner"
    | "employe"
    | "employe_read_only"
    | "admin"
    | "member"

interface Props {
    /**
     * Informations de l'utilisateur
     */
    user: User
    /**
     * Nom de la compagnie associée à l'utilisateur
     */
    company?: string
    /**
     * Adresse postale associée à l'utilisateur
     */
    address?: string
    /**
     * Taille de l'avatar
     */
    avatarSize?: "sm" | "md"
    /**
     * Cache l'adresse email
     */
    hideUsername?: boolean
    /**
     * Cache le numéro de téléphone
     */
    hidePhone?: boolean
    /**
     * Type de l'utilisateur (administrateur, owner, employe, etc.)
     */
    userType?: UserType
    /**
     * Champ personnalisé à afficher à droite
     */
    indicator?: string
    /**
     * Affiche une checkbox de sélection
     */
    checkbox?: boolean
    /**
     * État de la checkbox (v-model)
     */
    selected?: boolean
    /**
     * Désactive la checkbox
     */
    disabled?: boolean
}

interface Emits {
    /**
     * Émis lors du changement de sélection
     */
    (e: "update:selected", value: boolean): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    company: "",
    address: "",
    avatarSize: "sm",
    hideUsername: false,
    hidePhone: false,
    userType: undefined,
    indicator: "",
    checkbox: false,
    selected: false,
    disabled: false,
})

const emit = defineEmits<Emits>()

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Nom complet de l'utilisateur
 */
const fullName = computed(() => {
    const firstname = capitalize(props.user.firstname || "")
    const lastname = capitalize(props.user.lastname || "")
    return `${firstname} ${lastname}`.trim()
})

/**
 * Initiales de l'utilisateur pour l'avatar
 */
const initials = computed(() => {
    const firstname = props.user.firstname || ""
    const lastname = props.user.lastname || ""
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
})

/**
 * Classes CSS pour la taille de l'avatar
 */
const avatarSizeClasses = computed(() => {
    return props.avatarSize === "md"
        ? "w-16 h-16 text-base"
        : "w-10 h-10 text-sm"
})

/**
 * Détermine le type d'utilisateur effectif
 */
const getEffectiveUserType = computed((): UserType | undefined => {
    return props.userType
})

/**
 * Détermine la sévérité du tag selon le type d'utilisateur
 */
const getTagSeverity = computed(() => {
    const type = getEffectiveUserType.value
    if (type === "administrateur" || type === "admin" || type === "owner") {
        return "info"
    }
    return "secondary"
})

// ================================
// HELPERS
// ================================

/**
 * Met en majuscule la première lettre d'une chaîne
 */
function capitalize(str: string): string {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le changement de sélection
 */
function onCheckboxChange(value: boolean) {
    emit("update:selected", value)
}
</script>

<template>
    <div class="flex items-center gap-3 overflow-hidden">
        <!-- ================================ -->
        <!-- AVATAR -->
        <!-- ================================ -->
        <figure
            v-if="!user.img"
            class="flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-medium"
            :class="avatarSizeClasses"
        >
            {{ initials }}
        </figure>
        <figure
            v-else
            class="flex-shrink-0 rounded-full overflow-hidden"
            :class="avatarSizeClasses"
        >
            <img
                :src="user.img"
                :alt="fullName"
                class="w-full h-full object-cover"
            />
        </figure>

        <!-- ================================ -->
        <!-- INFORMATIONS -->
        <!-- ================================ -->
        <div class="flex flex-col justify-center items-start gap-0.5 min-w-0">
            <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-800 truncate">
                    {{ fullName }}
                </span>
                <BaseTag
                    v-if="getEffectiveUserType"
                    :value="t('userTypes.' + getEffectiveUserType)"
                    :severity="getTagSeverity"
                    :pt="{ label: 'text-xs' }"
                />
                <slot name="title" />
            </div>
            <p
                v-if="user.username && !hideUsername"
                class="text-xs text-gray-500 truncate"
            >
                {{ user.username.toLowerCase() }}
            </p>
            <p
                v-if="user.phone && !hidePhone"
                class="text-xs text-gray-500 truncate"
            >
                {{ user.phone }}
            </p>
            <p v-if="company" class="text-xs text-gray-500 truncate">
                {{ company }}
            </p>
            <p v-if="address" class="text-xs text-gray-500 truncate">
                {{ address }}
            </p>
        </div>

        <!-- ================================ -->
        <!-- ACTIONS (checkbox ou indicateur) -->
        <!-- ================================ -->
        <BaseCheckbox
            v-if="checkbox && !indicator"
            :model-value="selected"
            binary
            :disabled="disabled"
            class="ml-auto"
            @click.stop
            @update:model-value="onCheckboxChange"
        />
        <span
            v-else-if="indicator"
            class="ml-auto text-xs text-gray-500 truncate"
        >
            {{ indicator }}
        </span>

        <!-- ================================ -->
        <!-- SLOT PAR DÉFAUT -->
        <!-- ================================ -->
        <slot />
    </div>
</template>
